import React, { useMemo, useRef } from 'react';
import { Link } from 'react-router-dom';
import { validateAssayAnalytes, validateUploadMapping, type Assay, type AssayAnalytes, type UploadMapping } from '@mariana/labos-client';
import { useAuth } from '../../auth/AuthContext';
import { isManagedInCode } from '../../utils/assayDefinition';
import { AssayDefinitionEditor } from './AssayDefinitionEditor';
import { AssayMetadataPanel } from './AssayMetadataPanel';
import { UploadMappingBuilder } from './UploadMappingBuilder';
import { AssayAuditPanel } from './AssayAuditPanel';
import { PanelDefinitionPanel } from './PanelDefinitionPanel';
import './AssayDefinition.css';

interface Props {
  assay: Assay;
  onChanged: () => Promise<void>;
}

/**
 * The assay detail page's Definition tab: the analytes editor and, for
 * upload assays, the mapping builder on the left; metadata & lifecycle and
 * the worksheet-key audit on the right.
 */
export const AssayDefinitionTab: React.FC<Props> = ({ assay, onChanged }) => {
  const auth = useAuth();
  const assayId = assay.id!;
  const attrs = assay.attributes ?? {};

  // A stored definition the validator rejects (written before a rule tightened) opens in the JSON view as stored.
  const { definition, invalidDefinition } = useMemo<{ definition: AssayAnalytes | null; invalidDefinition?: unknown }>(() => {
    if (attrs.analytes == null) return { definition: null };
    const checked = validateAssayAnalytes(attrs.analytes);
    return 'value' in checked ? { definition: checked.value as AssayAnalytes } : { definition: null, invalidDefinition: attrs.analytes };
  }, [attrs.analytes]);

  const mapping = useMemo<UploadMapping | null>(() => {
    if (attrs.upload_mapping == null) return null;
    const checked = validateUploadMapping(attrs.upload_mapping);
    return 'value' in checked ? (checked.value as UploadMapping) : null;
  }, [attrs.upload_mapping]);

  // Bumped on every definition save so the audit panel re-reads with the new declarations.
  const definitionVersion = useRef(0);
  const lastDefinitionJson = useRef(JSON.stringify(attrs.analytes ?? null));
  const currentJson = JSON.stringify(attrs.analytes ?? null);
  if (currentJson !== lastDefinitionJson.current) {
    lastDefinitionJson.current = currentJson;
    definitionVersion.current += 1;
  }

  const managed = isManagedInCode(attrs.title);

  if (attrs.panel != null) {
    return (
      <div className="entity-detail-split assay-definition-split">
        <div className="entity-detail-split-main">
          <PanelDefinitionPanel assay={assay} isAdmin={auth?.isAdmin === true} onSaved={onChanged} />
        </div>
        <div className="entity-detail-split-sidebar">
          <AssayMetadataPanel assay={assay} isAdmin={auth?.isAdmin === true} onSaved={onChanged} />
        </div>
      </div>
    );
  }

  return (
    <div>
      {managed && (
        <div className="assay-banner">
          <span>⚠</span>
          <span>
            <strong>Managed in code.</strong> The definition of “{attrs.title}” is written by the seed
            (<code>seed-assay-analytes</code>), so edits made here last only until the next seed run. Change the code definition for anything permanent.
          </span>
        </div>
      )}
      <p className="assay-intro">
        One row per value a test of this assay reports, reads from the bench or computes; Capital OS and the upload parser follow
        what is declared here. Hover a column heading for what it means, or read <Link to="/assay/guide">how to define an assay</Link>.
      </p>
      <div className="entity-detail-split assay-definition-split">
        <div className="entity-detail-split-main">
          <AssayDefinitionEditor assayId={assayId} definition={definition} invalidDefinition={invalidDefinition} isAdmin={auth?.isAdmin === true} onSaved={onChanged} />
          {attrs.capture === 'upload' && (
            <UploadMappingBuilder assayId={assayId} definition={definition} mapping={mapping} onSaved={onChanged} />
          )}
        </div>
        <div className="entity-detail-split-sidebar">
          <AssayMetadataPanel assay={assay} isAdmin={auth?.isAdmin === true} onSaved={onChanged} />
          <AssayAuditPanel assayId={assayId} definitionVersion={definitionVersion.current} />
        </div>
      </div>
    </div>
  );
};
